'use client';

import React, { useState, useRef } from 'react';
import { motion, useScroll, useTransform } from 'motion/react';
import { Link } from 'react-router-dom';
import {
  Building2,
  Users,
  Package,
  ShieldCheck,
  Smartphone,
  FolderOpen,
  Search,
  Eye,
  CreditCard,
  BarChart3,
  ChevronDown,
  ArrowRight,
  CheckCircle,
  MapPin,
  Camera,
  Trash2,
  LayoutGrid,
  ShoppingCart,
  BadgePercent,
  DollarSign,
  Zap,
  Clock,
  TrendingUp,
  Shield,
  Layers,
  RefreshCw,
  Truck,
  FileText,
  Calculator,
  Plug,
  Play,
  Target,
  Sparkles,
  ChevronRight,
  ChevronLeft,
  Mail,
  Calendar,
  Bell,
  Slack,
  Github,
  Cloud,
  Database,
  MessageSquare,
  Workflow,
  Code,
  Power,
  Boxes,
  Brain,
  Home,
  Briefcase
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import realesyLogo from 'figma:asset/65b5f5f3ec5f3c50a9dd95caca3c0870f91a37b2.png';
import realesyFooterLogo from 'figma:asset/b72c5a9a60cfc0434d9cc663a0c34456298f505f.png';
import { ImageWithFallback } from '../components/figma/ImageWithFallback';
import { SmartReadIllustration } from '../components/SmartReadIllustration';
import { WorkDriveIllustration } from '../components/WorkDriveIllustration';
import { AppIcon } from '../components/AppIcon';
import type { IconName } from '../assets/icons';


// Use Cases Carousel Component
const UseCasesCarousel = () => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  const checkScrollability = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  const scroll = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const scrollAmount = 350; // Width of one card plus gap
      const newScrollLeft = direction === 'left'
        ? scrollContainerRef.current.scrollLeft - scrollAmount
        : scrollContainerRef.current.scrollLeft + scrollAmount;

      scrollContainerRef.current.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth'
      });
    }
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (scrollContainerRef.current) {
      setIsDragging(true);
      setStartX(e.pageX - scrollContainerRef.current.offsetLeft);
      setScrollLeft(scrollContainerRef.current.scrollLeft);
      scrollContainerRef.current.style.cursor = 'grabbing';
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !scrollContainerRef.current) return;
    e.preventDefault();
    const x = e.pageX - scrollContainerRef.current.offsetLeft;
    const walk = (x - startX) * 2; // Scroll speed multiplier
    scrollContainerRef.current.scrollLeft = scrollLeft - walk;
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    if (scrollContainerRef.current) {
      scrollContainerRef.current.style.cursor = 'grab';
    }
  };

  const handleMouseLeave = () => {
    if (isDragging) {
      setIsDragging(false);
      if (scrollContainerRef.current) {
        scrollContainerRef.current.style.cursor = 'grab';
      }
    }
  };

  React.useEffect(() => {
    checkScrollability();
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', checkScrollability);
      return () => container.removeEventListener('scroll', checkScrollability);
    }
  }, []);

  return (
    <div className="relative">
      {/* Left Arrow */}
      {canScrollLeft && (
        <button
          onClick={() => scroll('left')}
          className="absolute left-0 top-1/2 -translate-y-1/2 z-20 w-12 h-12 bg-white rounded-full shadow-lg border border-gray-200 flex items-center justify-center hover:bg-gray-50 transition-all hover:scale-110"
          aria-label="Scroll left"
        >
          <ChevronLeft className="w-6 h-6 text-gray-700" />
        </button>
      )}

      {/* Right Arrow */}
      {canScrollRight && (
        <button
          onClick={() => scroll('right')}
          className="absolute right-0 top-1/2 -translate-y-1/2 z-20 w-12 h-12 bg-white rounded-full shadow-lg border border-gray-200 flex items-center justify-center hover:bg-gray-50 transition-all hover:scale-110"
          aria-label="Scroll right"
        >
          <ChevronRight className="w-6 h-6 text-gray-700" />
        </button>
      )}

      <div
        ref={scrollContainerRef}
        className="overflow-x-auto pb-8 scrollbar-hide cursor-grab select-none"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
      >
        <div className="flex gap-6 min-w-max px-4">
          {/* Agents Card */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0 }}
            className="flex-shrink-0 w-80 flex flex-col bg-white rounded-2xl border border-gray-200 shadow-sm hover:shadow-md transition-all p-8"
          >
            <div className="w-16 h-16 rounded-xl bg-slate-100 flex items-center justify-center mb-5 p-2.5">
              <AppIcon name="officeMan" size={36} alt="Agents" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Agents</h3>
            <p className="text-gray-500 mb-5">Manage listings, not logistics</p>
            <ul className="space-y-3 mb-6 flex-1">
              <li className="flex items-start gap-2.5 text-gray-700">
                <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <span className="text-sm leading-relaxed">Order, track, and manage sign installations or removals in seconds.</span>
              </li>
              <li className="flex items-start gap-2.5 text-gray-700">
                <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <span className="text-sm leading-relaxed">Get photo proof, real-time updates, and reuse signs easily.</span>
              </li>
            </ul>
            <div className="flex items-center gap-1.5 text-blue-600 mt-auto">
              <span className="text-sm font-medium">Save time and focus on selling</span>
              <ArrowRight className="w-4 h-4" />
            </div>
          </motion.div>

          {/* Brokerages Card */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="flex-shrink-0 w-80 flex flex-col bg-white rounded-2xl border border-gray-200 shadow-sm hover:shadow-md transition-all p-8"
          >
            <div className="w-16 h-16 rounded-xl bg-slate-100 flex items-center justify-center mb-5 p-2.5">
              <AppIcon name="corporation" size={36} alt="Brokerages" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Brokerages</h3>
            <p className="text-gray-500 mb-5">Gain total visibility and brand control</p>
            <ul className="space-y-3 mb-6 flex-1">
              <li className="flex items-start gap-2.5 text-gray-700">
                <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <span className="text-sm leading-relaxed">Monitor sign inventory, spending, and approvals across offices.</span>
              </li>
              <li className="flex items-start gap-2.5 text-gray-700">
                <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <span className="text-sm leading-relaxed">Stay consistent with approved branding and vendor rules.</span>
              </li>
            </ul>
            <div className="flex items-center gap-1.5 text-blue-600 mt-auto">
              <span className="text-sm font-medium">Simplify operations and scale confidently</span>
              <ArrowRight className="w-4 h-4" />
            </div>
          </motion.div>

          {/* Decode AI & SmartShow AI Card */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="flex-shrink-0 w-80 flex flex-col bg-white rounded-2xl border border-gray-200 shadow-sm hover:shadow-md transition-all p-8"
          >
            <div className="w-16 h-16 rounded-xl bg-slate-100 flex items-center justify-center mb-5 p-2.5">
              <AppIcon name="ai" size={36} alt="Decode AI & SmartShow AI" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Decode AI & SmartShow AI</h3>
            <p className="text-gray-500 mb-5">Turn data and showings into intelligence</p>
            <ul className="space-y-3 mb-6 flex-1">
              <li className="flex items-start gap-2.5 text-gray-700">
                <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <span className="text-sm leading-relaxed">Decode AI summarizes files, communications, and job notes.</span>
              </li>
              <li className="flex items-start gap-2.5 text-gray-700">
                <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <span className="text-sm leading-relaxed">SmartShow AI captures showing feedback and buyer intent automatically.</span>
              </li>
            </ul>
            <div className="flex items-center gap-1.5 text-blue-600 mt-auto">
              <span className="text-sm font-medium">Work smarter with actionable insights</span>
              <ArrowRight className="w-4 h-4" />
            </div>
          </motion.div>

          {/* WorkDrive Collaboration Card */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
            className="flex-shrink-0 w-80 flex flex-col bg-white rounded-2xl border border-gray-200 shadow-sm hover:shadow-md transition-all p-8"
          >
            <div className="w-16 h-16 rounded-xl bg-slate-100 flex items-center justify-center mb-5 p-2.5">
              <AppIcon name="cloudFolder" size={36} alt="WorkDrive Collaboration" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">WorkDrive Collaboration</h3>
            <p className="text-gray-500 mb-5">Share and manage files securely</p>
            <ul className="space-y-3 mb-6 flex-1">
              <li className="flex items-start gap-2.5 text-gray-700">
                <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <span className="text-sm leading-relaxed">Upload sign artwork, branding, or listing photos with password protection, expiry limits, and full activity visibility.</span>
              </li>
            </ul>
            <div className="flex items-center gap-1.5 text-blue-600 mt-auto">
              <span className="text-sm font-medium">Keep every document organized and accessible</span>
              <ArrowRight className="w-4 h-4" />
            </div>
          </motion.div>

          {/* Vendors Card */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 }}
            className="flex-shrink-0 w-80 flex flex-col bg-white rounded-2xl border border-gray-200 shadow-sm hover:shadow-md transition-all p-8"
          >
            <div className="w-16 h-16 rounded-xl bg-slate-100 flex items-center justify-center mb-5 p-2.5">
              <AppIcon name="fifthVendor" size={36} alt="Vendors" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Vendors</h3>
            <p className="text-gray-500 mb-5">Streamline operations and deliver faster</p>
            <ul className="space-y-3 mb-6 flex-1">
              <li className="flex items-start gap-2.5 text-gray-700">
                <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <span className="text-sm leading-relaxed">Receive orders instantly, upload proof, and manage billing from one app.</span>
              </li>
              <li className="flex items-start gap-2.5 text-gray-700">
                <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <span className="text-sm leading-relaxed">Built-in integrations for shipping, payments, and taxes — no setup needed.</span>
              </li>
            </ul>
            <div className="flex items-center gap-1.5 text-blue-600 mt-auto">
              <span className="text-sm font-medium">Automate admin work and get paid quicker</span>
              <ArrowRight className="w-4 h-4" />
            </div>
          </motion.div>

          {/* Inventory & Assets Card */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.5 }}
            className="flex-shrink-0 w-80 flex flex-col bg-white rounded-2xl border border-gray-200 shadow-sm hover:shadow-md transition-all p-8"
          >
            <div className="w-16 h-16 rounded-xl bg-slate-100 flex items-center justify-center mb-5 p-2.5">
              <AppIcon name="fifthWarehouse" size={36} alt="Inventory & Assets" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Inventory & Assets</h3>
            <p className="text-gray-500 mb-5">Know what you own and reuse efficiently</p>
            <ul className="space-y-3 mb-6 flex-1">
              <li className="flex items-start gap-2.5 text-gray-700">
                <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <span className="text-sm leading-relaxed">Track signs, lockboxes, and other assets by location and condition.</span>
              </li>
              <li className="flex items-start gap-2.5 text-gray-700">
                <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <span className="text-sm leading-relaxed">Restocked items appear automatically in your inventory after removal.</span>
              </li>
            </ul>
            <div className="flex items-center gap-1.5 text-blue-600 mt-auto">
              <span className="text-sm font-medium">Reduce waste and cut costs</span>
              <ArrowRight className="w-4 h-4" />
            </div>
          </motion.div>
        </div>
      </div>

      {/* Scroll hint gradient overlays */}
      {canScrollRight && (
        <div className="absolute top-0 right-0 bottom-8 w-24 bg-gradient-to-l from-white to-transparent pointer-events-none"></div>
      )}
    </div>
  );
};

export default function HomePage() {
  const { scrollYProgress } = useScroll();
  const backgroundY = useTransform(scrollYProgress, [0, 1], ['0%', '50%']);

  const features = [
    {
      icon: MapPin,
      title: 'Sign Installation Management',
      description: 'Create, schedule, and track installations with real-time visibility and verified photo proof.',
      color: '#f58b01'
    },
    {
      icon: Trash2,
      title: 'Removals & Restocking',
      description: 'Schedule removals when listings close, get before-and-after photo proof, and automatically restock signs into your inventory.',
      color: '#3b82f6'
    },
    {
      icon: LayoutGrid,
      title: 'Inventory Control',
      description: 'See how many signs you have, their condition, and where they\'re stored.',
      color: '#8b5cf6'
    },
    {
      icon: ShoppingCart,
      title: 'Vendor Marketplace',
      description: 'Purchase signage, lockboxes, photography, and marketing materials from trusted real estate vendors — all in one place.',
      color: '#f58b01'
    },
    {
      icon: BadgePercent,
      title: 'Exclusive Vendor Discounts',
      description: 'Enjoy special pricing and volume savings negotiated for RealEsy users — helping brokerages and agents reduce overall marketing spend.',
      color: '#10b981'
    },
    {
      icon: DollarSign,
      title: 'Flexible Billing',
      description: 'Choose office-paid or agent-paid models with secure, PCI-compliant payments.',
      color: '#3b82f6'
    },
    {
      icon: FolderOpen,
      title: 'WorkDrive Integration',
      description: 'Share listing photos, artwork, and brand files securely with vendors or team members.',
      color: '#8b5cf6'
    },
    {
      icon: Smartphone,
      title: 'Mobile App Access',
      description: 'Manage installations, removals, and purchases on the go — full control, wherever you work.',
      color: '#f58b01'
    }
  ];

  const integrations = [
    {
      icon: Truck,
      title: 'Shipping & Logistics',
      description: 'UPS and other carriers for live shipping rates and tracking.',
      color: '#f58b01'
    },
    {
      icon: FileText,
      title: 'Sales Tax Automation',
      description: 'Integrated with TaxCloud and other leading sales tax platforms.',
      color: '#3b82f6'
    },
    {
      icon: Calculator,
      title: 'Accounting Software',
      description: 'Works with QuickBooks, Xero, and more for instant reconciliation.',
      color: '#8b5cf6'
    },
    {
      icon: CreditCard,
      title: 'Payment Gateways',
      description: 'Stripe, Authorize.Net, and other PCI-compliant processors built in.',
      color: '#10b981'
    },
    {
      icon: Layers,
      title: 'CRM & Productivity Tools',
      description: 'Plug into popular CRM and workflow systems — RealEsy keeps everything in sync automatically.',
      color: '#f59e0b'
    },
    {
      icon: Plug,
      title: 'Plug and Play',
      description: 'Prebuilt integrations mean no expensive development — just plug and play.',
      color: '#f58b01'
    }
  ];

  const benefits = [
    {
      icon: Zap,
      title: 'Simplify Vendor Management',
      description: 'Access all your vendors and services in one centralized marketplace.'
    },
    {
      icon: DollarSign,
      title: 'Control Costs',
      description: 'Transparent pricing, platform discounts, and full spend visibility.'
    },
    {
      icon: Eye,
      title: 'Gain Visibility',
      description: 'Track every order, payment, and product across offices and agents.'
    },
    {
      icon: Users,
      title: 'Empower Agents',
      description: 'Let agents order approved products directly while maintaining brand consistency.'
    },
    {
      icon: TrendingUp,
      title: 'Sell More, Manage Less',
      description: 'Reduce admin time so your team can focus on listings, clients, and closings.'
    },
    {
      icon: Target,
      title: 'Built for Real Estate',
      description: 'Purpose-built for real estate brokerages, not generic software.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-50 via-white to-blue-50 pt-32 pb-20 md:pt-40 md:pb-32 overflow-hidden">
        <motion.div
          style={{ y: backgroundY }}
          className="absolute inset-0 opacity-30"
        >
          <div className="absolute top-20 left-10 w-64 h-64 bg-blue-200 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-200 rounded-full blur-3xl"></div>
        </motion.div>

        {/* Floating Background Icons */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[
            { Icon: Building2, delay: 0, x: '10%', y: '15%' },
            { Icon: MapPin, delay: 0.5, x: '85%', y: '20%' },
            { Icon: Package, delay: 1, x: '15%', y: '70%' },
            { Icon: BarChart3, delay: 1.5, x: '80%', y: '75%' },
            { Icon: ShoppingCart, delay: 2, x: '50%', y: '10%' },
            { Icon: Smartphone, delay: 2.5, x: '90%', y: '50%' }
          ].map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0 }}
              animate={{
                opacity: [0, 0.08, 0],
                scale: [0, 1, 0],
                y: [0, -30, 0]
              }}
              transition={{
                duration: 5,
                repeat: Infinity,
                delay: item.delay,
                ease: "easeInOut"
              }}
              className="absolute"
              style={{
                left: item.x,
                top: item.y,
              }}
            >
              <item.Icon className="w-24 h-24 text-blue-300" />
            </motion.div>
          ))}
        </div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-[1500px] mx-auto">
            <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 xl:gap-20 items-center">
              {/* Left Column - Content */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                className="space-y-8 w-full"
              >
                <h1 className="text-5xl md:text-6xl lg:text-7xl xl:text-[4.75rem] font-bold leading-[1.08] tracking-tight">
                  <span className="text-gray-900 text-[2.5rem] md:text-[4.5rem] leading-snug">Real Estate Operations</span>
                  <br />
                  <span className="text-blue-600 text-[2.5rem] md:text-[4.5rem]">Simplified.</span>
                </h1>

                <p className="text-xl md:text-2xl text-gray-500 leading-relaxed max-w-2xl">
                  The modern platform connecting agents, brokerages, and vendors for signage, installations, and beyond.
                </p>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 }}
                  className="flex flex-col sm:flex-row sm:items-center gap-5 pt-2"
                >
                  <Link to="/contact">
                    <Button
                      size="lg"
                      className="text-lg md:text-xl px-10 py-7 h-auto rounded-xl text-white shadow-[0_8px_24px_rgba(245,139,1,0.35)] hover:shadow-[0_10px_28px_rgba(245,139,1,0.45)] transition-shadow"
                      style={{ backgroundColor: '#f58b01' }}
                    >
                      Get Started
                      <ArrowRight className="w-6 h-6 ml-2" />
                    </Button>
                  </Link>

                  <div className="flex items-center gap-3">
                    <div className="flex -space-x-2.5">
                      <div className="w-10 h-10 rounded-full bg-blue-500 border-2 border-white flex items-center justify-center text-white text-xs font-semibold">A</div>
                      <div className="w-10 h-10 rounded-full bg-indigo-500 border-2 border-white flex items-center justify-center text-white text-xs font-semibold">B</div>
                      <div className="w-10 h-10 rounded-full bg-sky-500 border-2 border-white flex items-center justify-center text-white text-xs font-semibold">C</div>
                    </div>
                    <span className="text-sm md:text-base font-medium text-blue-600">
                      +2K Trusted by 10K+ Agents
                    </span>
                  </div>
                </motion.div>

                {/* Stats — equally spaced across left column */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8 }}
                  className="grid grid-cols-3 w-full pt-8 mt-2 border-t border-gray-200"
                >
                  <div className="pr-4">
                    <div className="text-4xl md:text-5xl font-bold text-blue-600 mb-1.5">500+</div>
                    <div className="text-xs md:text-sm font-semibold tracking-wider uppercase text-gray-500">Brokerages</div>
                  </div>
                  <div className="px-4 border-l border-gray-200">
                    <div className="text-4xl md:text-5xl font-bold text-blue-600 mb-1.5">10K+</div>
                    <div className="text-xs md:text-sm font-semibold tracking-wider uppercase text-gray-500">Active Agents</div>
                  </div>
                  <div className="pl-4 border-l border-gray-200">
                    <div className="text-4xl md:text-5xl font-bold text-blue-600 mb-1.5">50K+</div>
                    <div className="text-xs md:text-sm font-semibold tracking-wider uppercase text-gray-500">Orders Managed</div>
                  </div>
                </motion.div>
              </motion.div>

              {/* Right Column - Image/Visual */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4, duration: 0.8 }}
                className="relative w-full max-w-[480px] lg:max-w-[600px] mx-auto lg:ml-auto"
              >
                {/* Main Dashboard Card */}
                <div className="relative">
                  <div className="absolute -top-8 -left-8 w-72 h-72 bg-gradient-to-br from-blue-400/20 to-blue-300/20 rounded-3xl blur-2xl"></div>
                  <div className="absolute -bottom-8 -right-8 w-72 h-72 bg-gradient-to-br from-orange-400/20 to-blue-300/20 rounded-3xl blur-2xl"></div>

                  <div className="relative bg-white rounded-2xl shadow-[0_20px_60px_rgba(15,23,42,0.12)] p-7 md:p-9 lg:p-10 border border-gray-100 min-h-[560px] lg:min-h-[620px] flex flex-col">
                    <div className="space-y-6 md:space-y-7 flex-1 flex flex-col">
                      {/* Header */}
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-sm md:text-base text-gray-400 mb-1">Dashboard Overview</div>
                          <h3 className="text-2xl md:text-3xl font-bold text-gray-900">Welcome back!</h3>
                        </div>
                        <div className="w-12 h-12 rounded-lg bg-blue-600 flex items-center justify-center">
                          <BarChart3 className="w-6 h-6 text-white" />
                        </div>
                      </div>

                      {/* Stats Grid */}
                      <div className="grid grid-cols-2 gap-4">
                        <motion.div
                          whileHover={{ scale: 1.02 }}
                          className="bg-white p-4 md:p-5 rounded-xl border border-gray-200 shadow-md flex items-center gap-3.5"
                        >
                          <AppIcon name="mapsAndLocation" size={56} alt="Active Installations" />
                          <div className="min-w-0">
                            <div className="text-2xl font-bold text-gray-900 leading-none mb-1.5">23</div>
                            <div className="text-xs md:text-sm text-gray-500 leading-snug">Active Installations</div>
                          </div>
                        </motion.div>
                        <motion.div
                          whileHover={{ scale: 1.02 }}
                          className="bg-white p-4 md:p-5 rounded-xl border border-gray-200 shadow-md flex items-center gap-3.5"
                        >
                          <AppIcon name="shippingAndDelivery" size={56} alt="Pending Orders" />
                          <div className="min-w-0">
                            <div className="text-2xl font-bold text-gray-900 leading-none mb-1.5">12</div>
                            <div className="text-xs md:text-sm text-gray-500 leading-snug">Pending Orders</div>
                          </div>
                        </motion.div>
                        <motion.div
                          whileHover={{ scale: 1.02 }}
                          className="bg-white p-4 md:p-5 rounded-xl border border-gray-200 shadow-md flex items-center gap-3.5"
                        >
                          <AppIcon name="warehouse" size={56} alt="Signs in Inventory" />
                          <div className="min-w-0">
                            <div className="text-2xl font-bold text-gray-900 leading-none mb-1.5">156</div>
                            <div className="text-xs md:text-sm text-gray-500 leading-snug">Signs in Inventory</div>
                          </div>
                        </motion.div>
                        <motion.div
                          whileHover={{ scale: 1.02 }}
                          className="bg-white p-4 md:p-5 rounded-xl border border-gray-200 shadow-md flex items-center gap-3.5"
                        >
                          <AppIcon name="checklist" size={56} alt="Completed Jobs" />
                          <div className="min-w-0">
                            <div className="text-2xl font-bold text-gray-900 leading-none mb-1.5">89</div>
                            <div className="text-xs md:text-sm text-gray-500 leading-snug">Completed Jobs</div>
                          </div>
                        </motion.div>
                      </div>

                      {/* Activity Chart Simulation */}
                      <div className="bg-white p-5 md:p-6 rounded-xl border border-gray-200 shadow-md flex-1 flex flex-col">
                        <div className="flex items-start justify-between gap-3 mb-4">
                          <div>
                            <div className="text-sm text-gray-400 mb-1">Activity this month</div>
                            <div className="text-3xl font-bold text-gray-900">2,847</div>
                          </div>
                          <div className="flex items-center rounded-full border border-gray-200 p-0.5 text-[10px] md:text-xs font-medium text-gray-500 bg-white">
                            <span className="px-2.5 py-1.5 rounded-full bg-blue-50 text-blue-600">Bars</span>
                            <span className="px-2.5 py-1.5 rounded-full">Dots</span>
                            <span className="px-2.5 py-1.5 rounded-full">Area</span>
                          </div>
                        </div>

                        <div className="grid grid-cols-3 gap-2 mb-5">
                          <div className="rounded-lg bg-stone-50 border border-stone-100 px-3 py-2">
                            <div className="text-[10px] md:text-xs text-gray-400 mb-0.5">Peak day</div>
                            <div className="text-sm font-bold text-gray-900">Thu</div>
                          </div>
                          <div className="rounded-lg bg-stone-50 border border-stone-100 px-3 py-2">
                            <div className="text-[10px] md:text-xs text-gray-400 mb-0.5">Avg / day</div>
                            <div className="text-sm font-bold text-gray-900">356</div>
                          </div>
                          <div className="rounded-lg bg-stone-50 border border-stone-100 px-3 py-2">
                            <div className="text-[10px] md:text-xs text-gray-400 mb-0.5">vs last month</div>
                            <div className="text-sm font-bold text-green-600">+12%</div>
                          </div>
                        </div>

                        <div className="flex gap-2 flex-1 min-h-[160px]">
                          <div className="flex flex-col justify-between text-[10px] text-gray-400 py-1 pb-6 shrink-0">
                            <span>600</span>
                            <span>400</span>
                            <span>200</span>
                            <span>0</span>
                          </div>
                          <div className="flex-1 flex items-end justify-between gap-1.5 h-40 md:h-44">
                            {[
                              { height: 72, label: 'Mon' },
                              { height: 108, label: 'Tue' },
                              { height: 64, label: 'Wed' },
                              { height: 128, label: 'Thu' },
                              { height: 88, label: 'Fri' },
                              { height: 116, label: 'Sat' },
                              { height: 140, label: 'Sun' },
                              { height: 100, label: 'Today', today: true },
                            ].map((bar, i) => (
                              <div key={i} className="flex-1 flex flex-col items-center justify-end gap-2 h-full">
                                <div className="w-full flex-1 flex items-end justify-center min-h-0">
                                  <motion.div
                                    initial={{ height: 0 }}
                                    animate={{ height: bar.height }}
                                    transition={{ delay: 1 + i * 0.1, duration: 0.5, ease: 'easeOut' }}
                                    className="w-full max-w-[28px] rounded-t-md"
                                    style={{
                                      backgroundColor: bar.today ? '#f58b01' : '#3b82f6',
                                      opacity: bar.today ? 1 : 0.9,
                                    }}
                                  />
                                </div>
                                <span
                                  className={`text-[10px] md:text-xs shrink-0 ${bar.today ? 'text-blue-600 font-semibold' : 'text-gray-400'
                                    }`}
                                >
                                  {bar.label}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Floating badges */}
                <motion.div
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 1.2 }}
                  className="absolute -top-8 -right-2 md:-right-8 bg-white rounded-xl shadow-lg px-5 py-3 border border-gray-100 min-w-[7.5rem]"
                >
                  <div className="flex items-center gap-2.5">
                    <div className="flex flex-col gap-0.5">
                      <TrendingUp className="w-6 h-6 text-green-600" />
                    </div>
                    <div className="flex items-center flex-col gap-1 text-green-600">
                      <div className="text-[13px] text-gray-400 leading-none">Growth</div>
                      <span className="text-sm font-semibold">+ 28%</span>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 1.4 }}
                  className="absolute -bottom-8 -left-2 md:-left-8 bg-white rounded-xl shadow-lg p-3 border border-gray-100"
                >
                  <div className="flex items-center gap-2.5">
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center">
                      <Zap className="w-6 h-6" style={{ color: '#f58b01' }} />
                    </div>
                    <div>
                      <div className="text-[13px] text-gray-400 leading-none mb-0.5">Fast</div>
                      <div className="text-sm font-medium text-gray-900">5 min setup</div>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* RealEsy for Brokerages & Agents - Tabbed Workflow Section */}
      <section className="py-20 bg-gradient-to-b from-white via-gray-50 to-white relative overflow-hidden border-y border-gray-200">
        {/* Dot Pattern Background */}
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: `radial-gradient(circle, #3b82f6 1px, transparent 1px)`,
            backgroundSize: '30px 30px'
          }}
        />

        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <div
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-white mb-4 shadow-md"
                style={{ background: 'linear-gradient(90deg, #236DD9 0%, #073399 100%)' }}
              >
                <Workflow className="w-4 h-4" />
                <span className="text-sm">Complete Workflow Solution</span>
              </div>
              <h2 className="text-4xl md:text-5xl text-gray-900 mb-3">
                RealEsy for Brokerages & Agents
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Your entire real estate operations — organized into one simple workflow.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <Tabs defaultValue="installations" className="w-full">
                <div className="rounded-2xl p-3 mb-8 bg-white shadow-lg">
                  <TabsList className="w-full flex flex-wrap justify-evenly gap-2 h-auto bg-transparent">
                    <TabsTrigger
                      value="installations"
                      className="flex-[0_0_calc(50%-0.5rem)] md:flex-[0_0_calc(50%-0.5rem)] lg:flex-initial px-6 lg:px-12 py-3 rounded-full text-sm lg:text-base data-[state=inactive]:bg-gray-100 data-[state=inactive]:text-gray-700 data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=active]:shadow-lg hover:shadow-md transition-all"
                    >
                      Installations
                    </TabsTrigger>
                    <TabsTrigger
                      value="removals"
                      className="flex-[0_0_calc(50%-0.5rem)] md:flex-[0_0_calc(50%-0.5rem)] lg:flex-initial px-6 lg:px-12 py-3 rounded-full text-sm lg:text-base data-[state=inactive]:bg-gray-100 data-[state=inactive]:text-gray-700 data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=active]:shadow-lg hover:shadow-md transition-all"
                    >
                      Removals
                    </TabsTrigger>
                    <TabsTrigger
                      value="inventory"
                      className="flex-[0_0_calc(50%-0.5rem)] md:flex-[0_0_calc(50%-0.5rem)] lg:flex-initial px-6 lg:px-12 py-3 rounded-full text-sm lg:text-base data-[state=inactive]:bg-gray-100 data-[state=inactive]:text-gray-700 data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=active]:shadow-lg hover:shadow-md transition-all"
                    >
                      Inventory
                    </TabsTrigger>
                    <TabsTrigger
                      value="marketplace"
                      className="flex-[0_0_calc(50%-0.5rem)] md:flex-[0_0_calc(50%-0.5rem)] lg:flex-initial px-6 lg:px-12 py-3 rounded-full text-sm lg:text-base data-[state=inactive]:bg-gray-100 data-[state=inactive]:text-gray-700 data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=active]:shadow-lg hover:shadow-md transition-all"
                    >
                      Vendor Marketplace
                    </TabsTrigger>
                    <TabsTrigger
                      value="workdrive"
                      className="flex-[0_0_calc(50%-0.5rem)] md:flex-[0_0_calc(50%-0.5rem)] lg:flex-initial px-6 lg:px-12 py-3 rounded-full text-sm lg:text-base data-[state=inactive]:bg-gray-100 data-[state=inactive]:text-gray-700 data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=active]:shadow-lg hover:shadow-md transition-all"
                    >
                      WorkDrive
                    </TabsTrigger>
                    <TabsTrigger
                      value="mobile"
                      className="flex-[0_0_calc(50%-0.5rem)] md:flex-[0_0_calc(50%-0.5rem)] lg:flex-initial px-6 lg:px-12 py-3 rounded-full text-sm lg:text-base data-[state=inactive]:bg-gray-100 data-[state=inactive]:text-gray-700 data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=active]:shadow-lg hover:shadow-md transition-all"
                    >
                      Mobile Access
                    </TabsTrigger>
                  </TabsList>
                </div>

                {/* Tab 1: Installations */}
                <TabsContent value="installations" className="mt-0">
                  <div className="bg-gray-50 rounded-2xl p-8 border border-gray-100">
                    <div className="grid lg:grid-cols-2 gap-10 items-start">
                      <div>
                        <h3 className="text-2xl text-gray-900 mb-3">
                          Manage Every Sign Job Seamlessly
                        </h3>
                        <p className="text-base text-gray-600 leading-relaxed mb-6">
                          Create, schedule, and track installations with real-time visibility and verified photo proof. Automate updates between agents, offices, and vendors.
                        </p>
                        <div className="space-y-3">
                          <div className="flex items-start gap-2">
                            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                            <span className="text-gray-700">Real-time job tracking</span>
                          </div>
                          <div className="flex items-start gap-2">
                            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                            <span className="text-gray-700">Photo proof & completion updates</span>
                          </div>
                          <div className="flex items-start gap-2">
                            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                            <span className="text-gray-700">Smart scheduling & priority tools</span>
                          </div>
                        </div>
                      </div>
                      <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6 border border-blue-200">
                        <div className="space-y-3">
                          <div className="bg-white rounded-lg p-3 shadow-sm border border-blue-100">
                            <div className="flex items-center gap-4 mb-1">
                              <AppIcon name="place" size={40} alt="Installation Order" />
                              <div className="flex-1">
                                <div className="text-xs text-gray-500">Installation Order</div>
                                <div className="text-sm text-gray-900">#INS-2847</div>
                                <div className="text-xs text-gray-600">123 Oak Street, Beverly Hills, CA</div>
                              </div>
                            </div>
                          </div>
                          <div className="bg-white rounded-lg p-3 shadow-sm border border-blue-100">
                            <div className="flex items-center gap-4 mb-1">
                              <AppIcon name="pictures" size={40} alt="Photo Proof" />
                              <div className="flex-1">
                                <div className="text-xs text-gray-500">Photo Proof</div>
                                <div className="text-sm text-gray-900">Verified Complete</div>
                                <div className="text-sm text-green-600">✓ Sign installed successfully</div>
                              </div>
                            </div>
                          </div>
                          <div className="bg-white rounded-xl p-4 shadow-sm border border-blue-100">
                            <div className="flex items-center gap-4">
                              <AppIcon name="schedule" size={40} alt="Next Schedule" />
                              <div className="flex-1">
                                <div className="text-sm text-gray-500">Next Schedule</div>
                                <div className="text-gray-900">Tomorrow 10:00 AM</div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </TabsContent>

                {/* Tab 2: Removals */}
                <TabsContent value="removals" className="mt-0">
                  <div className="bg-gray-50 rounded-3xl p-12 border border-gray-100" style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
                    <div className="grid lg:grid-cols-2 gap-12 items-start">
                      <div>
                        <h3 className="text-3xl text-gray-900 mb-4">
                          Simplify Removals & Restocking
                        </h3>
                        <p className="text-lg text-gray-600 leading-relaxed mb-8">
                          Schedule removals when listings close, get before-and-after photo proof, and automatically restock signs into your inventory for reuse.
                        </p>
                        <div className="space-y-4">
                          <div className="flex items-start gap-3">
                            <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                            <span className="text-gray-700 text-lg">Auto restocking after removals</span>
                          </div>
                          <div className="flex items-start gap-3">
                            <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                            <span className="text-gray-700 text-lg">Track sign condition for reuse or replacement</span>
                          </div>
                          <div className="flex items-start gap-3">
                            <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                            <span className="text-gray-700 text-lg">Ready-to-use signs for the next listing</span>
                          </div>
                        </div>
                      </div>
                      <div className="bg-gradient-to-br from-teal-50 to-teal-100 rounded-2xl p-8 border border-teal-200">
                        <div className="space-y-4">
                          <div className="bg-white rounded-xl p-4 shadow-sm border border-teal-100">
                            <div className="flex items-center gap-4 mb-2">
                              <AppIcon name="reload" size={40} alt="Removal Request" />
                              <div className="flex-1">
                                <div className="text-sm text-gray-500">Removal Request</div>
                                <div className="text-gray-900">Listing Sold</div>
                                <div className="text-sm text-gray-600">456 Maple Ave, Los Angeles, CA</div>
                              </div>
                            </div>
                          </div>
                          <div className="bg-white rounded-xl p-4 shadow-sm border border-teal-100">
                            <div className="flex items-center gap-4 mb-2">
                              <AppIcon name="stock" size={40} alt="Auto Restock" />
                              <div className="flex-1">
                                <div className="text-sm text-gray-500">Auto Restock</div>
                                <div className="text-gray-900">Condition: Excellent</div>
                                <div className="text-sm text-green-600">✓ Added to inventory</div>
                              </div>
                            </div>
                          </div>
                          <div className="bg-white rounded-xl p-4 shadow-sm border border-teal-100">
                            <div className="flex items-center gap-4">
                              <AppIcon name="inventory" size={40} alt="Inventory Updated" />
                              <div className="flex-1">
                                <div className="text-sm text-gray-500">Inventory Updated</div>
                                <div className="text-gray-900">+1 For Sale Sign</div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </TabsContent>

                {/* Tab 3: Inventory */}
                <TabsContent value="inventory" className="mt-0">
                  <div className="bg-gray-50 rounded-3xl p-12 border border-gray-100" style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
                    <div className="grid lg:grid-cols-2 gap-12 items-start">
                      <div>
                        <h3 className="text-3xl text-gray-900 mb-4">
                          Know Where Every Sign Is — Instantly
                        </h3>
                        <p className="text-lg text-gray-600 leading-relaxed mb-8">
                          View sign quantities, conditions, and storage locations across all offices and agents. Restock or purchase new signs directly from your dashboard.
                        </p>
                        <div className="space-y-4">
                          <div className="flex items-start gap-3">
                            <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                            <span className="text-gray-700 text-lg">Real-time inventory sync</span>
                          </div>
                          <div className="flex items-start gap-3">
                            <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                            <span className="text-gray-700 text-lg">Condition-based management</span>
                          </div>
                          <div className="flex items-start gap-3">
                            <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                            <span className="text-gray-700 text-lg">Direct sign purchasing</span>
                          </div>
                        </div>
                      </div>
                      <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-2xl p-8 border border-green-200">
                        <div className="space-y-4">
                          <div className="bg-white rounded-xl p-4 shadow-sm border border-green-100">
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center gap-4">
                                <AppIcon name="sign" size={40} alt="For Sale Signs" />
                                <div className="text-gray-900">For Sale Signs</div>
                              </div>
                              <div className="text-2xl text-green-600">24</div>
                            </div>
                            <div className="grid grid-cols-2 gap-2 text-sm">
                              <div className="text-gray-600">Excellent: 18</div>
                              <div className="text-gray-600">Good: 6</div>
                            </div>
                          </div>
                          <div className="bg-white rounded-xl p-4 shadow-sm border border-green-100">
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center gap-4">
                                <AppIcon name="sign" size={40} alt="Sold Signs" />
                                <div className="text-gray-900">Sold Signs</div>
                              </div>
                              <div className="text-2xl text-blue-600">12</div>
                            </div>
                            <div className="grid grid-cols-2 gap-2 text-sm">
                              <div className="text-gray-600">Excellent: 8</div>
                              <div className="text-gray-600">Good: 4</div>
                            </div>
                          </div>
                          <div className="bg-white rounded-xl p-4 shadow-sm border border-green-100">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-4">
                                <AppIcon name="sign" size={40} alt="Open House Signs" />
                                <div className="text-gray-900">Open House Signs</div>
                              </div>
                              <div className="text-2xl text-purple-600">8</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </TabsContent>

                {/* Tab 4: Vendor Marketplace */}
                <TabsContent value="marketplace" className="mt-0">
                  <div className="bg-gray-50 rounded-3xl p-12 border border-gray-100" style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
                    <div className="grid lg:grid-cols-2 gap-12 items-start">
                      <div>
                        <h3 className="text-3xl text-gray-900 mb-4">
                          Purchase from Trusted Real Estate Vendors
                        </h3>
                        <p className="text-lg text-gray-600 leading-relaxed mb-8">
                          Order signage, lockboxes, staging, photography, and more — all from trusted partners in one centralized marketplace.
                        </p>
                        <div className="space-y-4">
                          <div className="flex items-start gap-3">
                            <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                            <span className="text-gray-700 text-lg">Transparent pricing & volume discounts</span>
                          </div>
                          <div className="flex items-start gap-3">
                            <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                            <span className="text-gray-700 text-lg">Exclusive platform offers (like 5% off with Farmer Signs)</span>
                          </div>
                          <div className="flex items-start gap-3">
                            <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                            <span className="text-gray-700 text-lg">Simple checkout & spend tracking</span>
                          </div>
                        </div>
                      </div>
                      <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-2xl p-8 border border-orange-200">
                        <div className="space-y-4">
                          <div className="bg-white rounded-xl p-4 shadow-sm border border-orange-100">
                            <div className="flex items-center gap-4 mb-2">
                              <AppIcon name="vendor" size={40} alt="Vendor" />
                              <div className="flex-1">
                                <div className="text-sm text-gray-500">Vendor</div>
                                <div className="text-gray-900">Farmer Signs</div>
                                <div className="flex items-center justify-between">
                                  <div className="text-sm text-gray-600">For Sale Signs (10 pack)</div>
                                  <div className="text-green-600">5% off</div>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="bg-white rounded-xl p-4 shadow-sm border border-orange-100">
                            <div className="flex items-center gap-4 mb-2">
                              <AppIcon name="camera" size={40} alt="Service" />
                              <div className="flex-1">
                                <div className="text-sm text-gray-500">Service</div>
                                <div className="text-gray-900">Pro Photography</div>
                                <div className="text-sm text-green-600">Premium listing package</div>
                              </div>
                            </div>
                          </div>
                          <div className="bg-white rounded-xl p-4 shadow-sm border border-orange-100">
                            <div className="flex items-center gap-4">
                              <AppIcon name="productManagement" size={40} alt="Product" />
                              <div className="flex-1">
                                <div className="text-sm text-gray-500">Product</div>
                                <div className="text-gray-900">Lockbox (5 units)</div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </TabsContent>

                {/* Tab 5: WorkDrive */}
                <TabsContent value="workdrive" className="mt-0">
                  <div className="bg-gray-50 rounded-3xl p-12 border border-gray-100" style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
                    <div className="grid lg:grid-cols-2 gap-12 items-start">
                      <div>
                        <h3 className="text-3xl text-gray-900 mb-4">
                          Collaborate & Share Files Securely
                        </h3>
                        <p className="text-lg text-gray-600 leading-relaxed mb-8">
                          Upload, organize, and share brand assets, sign designs, or marketing templates with full control. Manage file access, permissions, and activity logs — no more lost email attachments.
                        </p>
                        <div className="space-y-4">
                          <div className="flex items-start gap-3">
                            <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                            <span className="text-gray-700 text-lg">Password protection & expiry limits</span>
                          </div>
                          <div className="flex items-start gap-3">
                            <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                            <span className="text-gray-700 text-lg">Activity visibility & version tracking</span>
                          </div>
                          <div className="flex items-start gap-3">
                            <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                            <span className="text-gray-700 text-lg">Team folders for agents & managers</span>
                          </div>
                        </div>
                      </div>
                      <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-2xl p-8 border border-indigo-200">
                        <div className="space-y-4">
                          <div className="bg-white rounded-xl p-4 shadow-sm border border-indigo-100">
                            <div className="flex items-center gap-4 mb-2">
                              <AppIcon name="folder" size={40} alt="Folder" />
                              <div className="flex-1">
                                <div className="text-sm text-gray-500">Folder</div>
                                <div className="text-gray-900">Brand Assets</div>
                                <div className="text-sm text-gray-600">24 files • Shared with team</div>
                              </div>
                            </div>
                          </div>
                          <div className="bg-white rounded-xl p-4 shadow-sm border border-indigo-100">
                            <div className="flex items-center gap-4 mb-2">
                              <AppIcon name="colorPalette" size={40} alt="Document" />
                              <div className="flex-1">
                                <div className="text-sm text-gray-500">Document</div>
                                <div className="text-gray-900">Sign Design Template</div>
                                <div className="text-sm text-green-600">✓ Password protected</div>
                              </div>
                            </div>
                          </div>
                          <div className="bg-white rounded-xl p-4 shadow-sm border border-indigo-100">
                            <div className="flex items-center gap-4">
                              <AppIcon name="shield" size={40} alt="Security" />
                              <div className="flex-1">
                                <div className="text-sm text-gray-500">Security</div>
                                <div className="text-gray-900">Access Controlled</div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </TabsContent>

                {/* Tab 6: Mobile Access */}
                <TabsContent value="mobile" className="mt-0">
                  <div className="bg-gray-50 rounded-3xl p-12 border border-gray-100" style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
                    <div className="grid lg:grid-cols-2 gap-12 items-start">
                      <div>
                        <h3 className="text-3xl text-gray-900 mb-4">
                          Work Anywhere, Anytime
                        </h3>
                        <p className="text-lg text-gray-600 leading-relaxed mb-8">
                          Create jobs, track progress, approve removals, and manage inventory directly from your phone.
                        </p>
                        <div className="space-y-4">
                          <div className="flex items-start gap-3">
                            <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                            <span className="text-gray-700 text-lg">Full mobile functionality</span>
                          </div>
                          <div className="flex items-start gap-3">
                            <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                            <span className="text-gray-700 text-lg">Real-time notifications</span>
                          </div>
                          <div className="flex items-start gap-3">
                            <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                            <span className="text-gray-700 text-lg">Instant access to job updates & proof</span>
                          </div>
                        </div>
                      </div>
                      <div className="bg-gradient-to-br from-cyan-50 to-cyan-100 rounded-2xl p-8 border border-cyan-200">
                        <div className="space-y-4">
                          <div className="bg-white rounded-xl p-4 shadow-sm border border-cyan-100">
                            <div className="flex items-center gap-4 mb-2">
                              <AppIcon name="mobilePhone" size={40} alt="Mobile App" />
                              <div className="flex-1">
                                <div className="text-sm text-gray-500">Mobile App</div>
                                <div className="text-gray-900">iOS & Android</div>
                                <div className="text-sm text-gray-600">Full feature parity</div>
                              </div>
                            </div>
                          </div>
                          <div className="bg-white rounded-xl p-4 shadow-sm border border-cyan-100">
                            <div className="flex items-center gap-4 mb-2">
                              <AppIcon name="notification" size={40} alt="Notification" />
                              <div className="flex-1">
                                <div className="text-sm text-gray-500">Notification</div>
                                <div className="text-gray-900">Installation Complete</div>
                                <div className="text-sm text-green-600">✓ Photo proof attached</div>
                              </div>
                            </div>
                          </div>
                          <div className="bg-white rounded-xl p-4 shadow-sm border border-cyan-100">
                            <div className="flex items-center gap-4">
                              <AppIcon name="flash" size={40} alt="Quick Actions" />
                              <div className="flex-1">
                                <div className="text-sm text-gray-500">Quick Actions</div>
                                <div className="text-gray-900">Create job in seconds</div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </TabsContent>
              </Tabs>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Carousel Integrations Section */}
      <section className="py-24 bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900 relative overflow-hidden">
        {/* Animated Grid Pattern Background */}
        <div className="absolute inset-0 opacity-10">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `linear-gradient(rgba(245, 139, 1, 0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(245, 139, 1, 0.3) 1px, transparent 1px)`,
              backgroundSize: '50px 50px'
            }}
          />
        </div>

        {/* Gradient overlays */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-600/20 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-orange-500/20 rounded-full blur-3xl"></div>
        </div>

        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-orange-500/20 to-blue-500/20 text-white border-2 border-orange-500/50 mb-6 backdrop-blur-sm">
                <Plug className="w-4 h-4" />
                <span className="text-sm">Seamless Integrations</span>
              </div>
              <h2 className="text-3xl md:text-4xl lg:text-5xl text-white mb-6 drop-shadow-2xl">
                Prebuilt Integrations That <span style={{ color: '#f58b01' }}>Just Work</span>
              </h2>
              <p className="text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
                Connect with the tools your team already uses. No complex setup, no coding required.
              </p>
            </motion.div>

            {/* Scrolling Integration Rows */}
            <div className="space-y-8">
              {/* Row 1 - Scrolls Left to Right */}
              <div className="relative overflow-hidden">
                <motion.div
                  className="flex gap-6"
                  animate={{
                    x: ['-50%', '0%'],
                  }}
                  transition={{
                    duration: 30,
                    repeat: Infinity,
                    ease: "linear",
                  }}
                >
                  {/* Duplicate the integrations for seamless loop */}
                  {[...Array(2)].map((_, setIndex) => (
                    <React.Fragment key={setIndex}>
                      {[
                        { icon: 'googleDrive' as IconName, name: 'Google Drive' },
                        { icon: 'salesforce' as IconName, name: 'Salesforce' },
                        { icon: 'outlook' as IconName, name: 'Outlook' },
                        { icon: 'slack' as IconName, name: 'Slack' },
                        { icon: 'zendesk' as IconName, name: 'Zendesk' },
                        { icon: 'tableau' as IconName, name: 'Tableau' },
                        { icon: 'github' as IconName, name: 'GitHub' },
                        { icon: 'vscode' as IconName, name: 'VS Code' },
                      ].map((integration, index) => (
                        <div
                          key={`${setIndex}-${index}`}
                          className="flex-shrink-0 w-48 h-32 bg-white rounded-2xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow flex items-center justify-center group"
                        >
                          <div className="text-center">
                            <div className="mx-auto mb-3 flex items-center justify-center transition-transform group-hover:scale-110">
                              <AppIcon name={integration.icon} size={40} alt={integration.name} />
                            </div>
                            <div className="text-sm text-gray-700">{integration.name}</div>
                          </div>
                        </div>
                      ))}
                    </React.Fragment>
                  ))}
                </motion.div>
              </div>

              {/* Row 2 - Scrolls Right to Left */}
              <div className="relative overflow-hidden">
                <motion.div
                  className="flex gap-6"
                  animate={{
                    x: ['0%', '-50%'],
                  }}
                  transition={{
                    duration: 35,
                    repeat: Infinity,
                    ease: "linear",
                  }}
                >
                  {/* Duplicate the integrations for seamless loop */}
                  {[...Array(2)].map((_, setIndex) => (
                    <React.Fragment key={setIndex}>
                      {[
                        { icon: 'asana' as IconName, name: 'Asana' },
                        { icon: 'vimeo' as IconName, name: 'Vimeo' },
                        { icon: 'hubspot' as IconName, name: 'HubSpot' },
                        { icon: 'shopify' as IconName, name: 'Shopify' },
                        { icon: 'stripe' as IconName, name: 'Stripe' },
                        { icon: 'twilio' as IconName, name: 'Twilio' },
                        { icon: 'calendly' as IconName, name: 'Calendly' },
                        { icon: 'notion' as IconName, name: 'Notion' },
                      ].map((integration, index) => (
                        <div
                          key={`${setIndex}-${index}`}
                          className="flex-shrink-0 w-48 h-32 bg-white rounded-2xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow flex items-center justify-center group"
                        >
                          <div className="text-center">
                            <div className="mx-auto mb-3 flex items-center justify-center transition-transform group-hover:scale-110">
                              <AppIcon name={integration.icon} size={40} alt={integration.name} />
                            </div>
                            <div className="text-sm text-gray-700">{integration.name}</div>
                          </div>
                        </div>
                      ))}
                    </React.Fragment>
                  ))}
                </motion.div>
              </div>

              {/* Row 3 - Scrolls Left to Right */}
              <div className="relative overflow-hidden">
                <motion.div
                  className="flex gap-6"
                  animate={{
                    x: ['-50%', '0%'],
                  }}
                  transition={{
                    duration: 32,
                    repeat: Infinity,
                    ease: "linear",
                  }}
                >
                  {/* Duplicate the integrations for seamless loop */}
                  {[...Array(2)].map((_, setIndex) => (
                    <React.Fragment key={setIndex}>
                      {[
                        { icon: 'zapier' as IconName, name: 'Zapier' },
                        { icon: 'quickbooks' as IconName, name: 'QuickBooks' },
                        { icon: 'googleAnalytics' as IconName, name: 'Google Analytics' },
                        { icon: 'auth0' as IconName, name: 'Auth0' },
                        { icon: 'airtable' as IconName, name: 'Airtable' },
                        { icon: 'dropbox' as IconName, name: 'Dropbox' },
                        { icon: 'jira' as IconName, name: 'Jira' },
                      ].map((integration, index) => (
                        <div
                          key={`${setIndex}-${index}`}
                          className="flex-shrink-0 w-48 h-32 bg-white rounded-2xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow flex items-center justify-center group"
                        >
                          <div className="text-center">
                            <div className="mx-auto mb-3 flex items-center justify-center transition-transform group-hover:scale-110">
                              <AppIcon name={integration.icon} size={40} alt={integration.name} />
                            </div>
                            <div className="text-sm text-gray-700">{integration.name}</div>
                          </div>
                        </div>
                      ))}
                    </React.Fragment>
                  ))}
                </motion.div>
              </div>
            </div>

            {/* Description below carousel */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5 }}
              className="text-center mt-12"
            >
              <p className="text-gray-300 text-lg max-w-2xl mx-auto">
                Connect seamlessly with your favorite tools and platforms. From CRM to accounting software, everything syncs automatically.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* SmartRead AI Section */}
      {/* <section className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">

              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="relative"
              >
                <SmartReadIllustration />
              </motion.div>


              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-16 h-16 rounded-2xl flex items-center justify-center shadow-xl border-2 border-orange-300" style={{ background: 'linear-gradient(135deg, #fff5eb 0%, #ffe4cc 100%)' }}>
                    <Brain className="w-8 h-8" style={{ color: '#f58b01' }} />
                  </div>
                  <div className="px-5 py-2.5 bg-gradient-to-r from-orange-100 to-amber-100 text-orange-800 rounded-full text-sm border-2 border-orange-300 shadow-lg">
                    <Sparkles className="w-4 h-4 inline mr-1" />
                    AI-Powered
                  </div>
                </div>

                <h2 className="text-3xl md:text-4xl lg:text-5xl text-gray-900 mb-6">
                  RealEsy SmartRead AI — Your AI Document Assistant
                </h2>

                <div className="space-y-4 mb-8">
                  <p className="text-lg text-gray-700 leading-relaxed">
                    Turn lengthy contracts and disclosures into clear, actionable summaries in seconds.
                  </p>
                  <p className="text-lg text-gray-700 leading-relaxed">
                    SmartRead AI extracts key terms, dates, and contingencies automatically — so agents and brokerages never miss a detail.
                  </p>
                  <p className="text-lg text-gray-700 leading-relaxed">
                    Ask any question about your document, get instant answers, and share professional summaries with clients directly from RealEsy.
                  </p>
                </div>

                <div className="flex items-center gap-2 mb-8 p-6 bg-gradient-to-r from-orange-100 to-amber-100 rounded-2xl border-2 border-orange-300 shadow-xl">
                  <ArrowRight className="w-6 h-6 flex-shrink-0" style={{ color: '#f58b01' }} />
                  <p className="text-gray-900 text-lg">
                    Work smarter, close faster, and stay fully informed.
                  </p>
                </div>

                <Link to="/features/smartread">
                  <Button
                    size="lg"
                    className="text-white px-8 py-6 text-lg rounded-xl"
                    style={{ backgroundColor: '#f58b01' }}
                    onMouseEnter={(e: React.MouseEvent<HTMLButtonElement>) => e.currentTarget.style.backgroundColor = '#d97700'}
                    onMouseLeave={(e: React.MouseEvent<HTMLButtonElement>) => e.currentTarget.style.backgroundColor = '#f58b01'}
                  >
                    Learn More
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
              </motion.div>
            </div>
          </div>
        </div>
      </section> */}

      {/* WorkDrive Section */}
      <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-6">
          <div className="max-w-[1440px] mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)] gap-16 items-center">
              {/* Left Side - Content */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-16 h-16 rounded-2xl flex items-center justify-center bg-white shadow-xl border-2 border-gray-200">
                    <AppIcon name="homeWorkdriveFolder01" size={32} alt="WorkDrive" />
                  </div>
                  <div className="px-5 py-2.5 bg-gradient-to-r from-blue-100 to-indigo-100 text-blue-800 rounded-full text-sm border-2 border-blue-300 shadow-lg">
                    <Shield className="w-4 h-4 inline mr-1" />
                    Secure Storage
                  </div>
                </div>

                <h2 className="text-3xl md:text-4xl lg:text-5xl text-gray-900 mb-6">
                  WorkDrive — Secure, Listing-First File Management
                </h2>

                <p className="text-lg text-gray-700 leading-relaxed mb-8">
                  Keep every listing's photos, contracts, and proofs in one place—built right into RealEsy.
                </p>

                <div className="space-y-4 mb-8">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-1" />
                    <div>
                      <div className="text-gray-900 mb-1">Listing-centric folders</div>
                      <div className="text-sm text-gray-600">Office → Team → Listing, so nothing gets lost.</div>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-1" />
                    <div>
                      <div className="text-gray-900 mb-1">Secure sharing</div>
                      <div className="text-sm text-gray-600">Passwords, expiry dates, view/edit permissions.</div>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-1" />
                    <div>
                      <div className="text-gray-900 mb-1">Team folders & roles</div>
                      <div className="text-sm text-gray-600">Owner/Admin/Editor/Viewer with activity logs.</div>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-1" />
                    <div>
                      <div className="text-gray-900 mb-1">Attach to workflows</div>
                      <div className="text-sm text-gray-600">Pin files to sales orders, installations, and removals.</div>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-1" />
                    <div>
                      <div className="text-gray-900 mb-1">Smart search & AI summaries</div>
                      <div className="text-sm text-gray-600">Find key terms/dates without opening the file.</div>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-1" />
                    <div>
                      <div className="text-gray-900 mb-1">Mobile-ready</div>
                      <div className="text-sm text-gray-600">Capture on site, upload, and share from your phone.</div>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2 mb-8 p-6 bg-gradient-to-r from-blue-100 to-indigo-100 rounded-2xl border-2 border-blue-300 shadow-xl">
                  <ArrowRight className="w-6 h-6 flex-shrink-0 text-blue-600" />
                  <p className="text-gray-900 text-lg">
                    Organize once. Share securely. Work faster.
                  </p>
                </div>

                <Link to="/features/workdrive">
                  <Button
                    size="lg"
                    className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 text-lg rounded-xl"
                  >
                    Learn More
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
              </motion.div>

              {/* Right Side - Illustration */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="relative"
              >
                <WorkDriveIllustration />
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-6">
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <h2 className="text-3xl md:text-4xl lg:text-5xl text-gray-900 mb-6">
                See How RealEsy Keeps Work Moving<br />Across Every Workflow
              </h2>
              <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
                From installations to AI-powered insights, RealEsy keeps brokerages, agents, and vendors aligned — every step of the way.
              </p>
            </motion.div>

            {/* Horizontal Scrollable Use Case Cards */}
            <UseCasesCarousel />

            {/* CTA Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.6 }}
              className="text-center mt-16"
            >
              <p className="text-xl text-gray-700 mb-6">
                RealEsy connects it all — from sign to sale, powered by AI and collaboration.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/contact">
                  <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 text-lg rounded-xl">
                    Book a Demo
                  </Button>
                </Link>
                <Button size="lg" variant="outline" className="border-gray-300 text-gray-700 hover:bg-gray-50 px-8 py-6 text-lg rounded-xl">
                  Explore Features
                </Button>
              </div>
            </motion.div>
          </div>
        </div>

        <style dangerouslySetInnerHTML={{
          __html: `
          .scrollbar-hide::-webkit-scrollbar {
            display: none;
          }
          .scrollbar-hide {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
        ` }} />
      </section>

      {/* What Sets RealEsy Apart Section */}
      <section className="py-24 bg-gradient-to-br from-blue-50 via-white to-blue-50 relative overflow-hidden">
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-20 left-10 w-64 h-64 bg-blue-200 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-200 rounded-full blur-3xl"></div>
        </div>

        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl lg:text-5xl text-gray-900 mb-6">
                What Sets RealEsy Apart?
              </h2>
              <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
                RealEsy isn't just another operations tool — it's the first connected real estate platform that unites brokerages, agents, and vendors under one ecosystem.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {/* Feature 1: Built for Real Estate */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0 }}
                whileHover={{ y: -5, scale: 1.02 }}
                className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all border border-gray-100"
              >
                <div className="w-[4.5rem] h-[4.5rem] rounded-xl bg-white border border-gray-200 shadow-md flex items-center justify-center mb-6 p-2">
                  <AppIcon name="work" size={44} alt="Built for Real Estate" />
                </div>
                <h3 className="text-xl text-gray-900 mb-4">Built for Real Estate, Not Generic Workflows</h3>
                <p className="text-gray-600 leading-relaxed">
                  Purpose-built for how brokerages and agents actually work — from signage and installations to vendor management and billing.
                </p>
              </motion.div>

              {/* Feature 2: Connected Ecosystem */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                whileHover={{ y: -5, scale: 1.02 }}
                className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all border border-gray-100"
              >
                <div className="w-[4.5rem] h-[4.5rem] rounded-xl bg-white border border-gray-200 shadow-md flex items-center justify-center mb-6 p-2">
                  <AppIcon name="connections" size={44} alt="Connected Ecosystem" />
                </div>
                <h3 className="text-xl text-gray-900 mb-4">Connected Ecosystem for Everyone</h3>
                <p className="text-gray-600 leading-relaxed">
                  Agents, offices, and vendors operate in sync — every job, order, and update flows through one shared platform.
                </p>
              </motion.div>

              {/* Feature 3: Exclusive Partner Discounts */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                whileHover={{ y: -5, scale: 1.02 }}
                className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all border border-gray-100"
              >
                <div className="w-[4.5rem] h-[4.5rem] rounded-xl bg-white border border-gray-200 shadow-md flex items-center justify-center mb-6 p-2">
                  <AppIcon name="sale" size={44} alt="Exclusive Partner Discounts" />
                </div>
                <h3 className="text-xl text-gray-900 mb-4">Exclusive Partner Discounts</h3>
                <p className="text-gray-600 leading-relaxed">
                  Access real, platform-level savings like 5% off sign orders from Farmer Signs, plus volume-based vendor deals across categories.
                </p>
              </motion.div>

              {/* Feature 4: Plug-and-Play Integrations */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
                whileHover={{ y: -5, scale: 1.02 }}
                className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all border border-gray-100"
              >
                <div className="w-[4.5rem] h-[4.5rem] rounded-xl bg-white border border-gray-200 shadow-md flex items-center justify-center mb-6 p-2">
                  <AppIcon name="noConnection" size={44} alt="Plug-and-Play Integrations" />
                </div>
                <h3 className="text-xl text-gray-900 mb-4">Plug-and-Play Integrations</h3>
                <p className="text-gray-600 leading-relaxed">
                  Instant connections with UPS, TaxCloud, QuickBooks, Stripe, and CRM tools — no developer setup required.
                </p>
              </motion.div>

              {/* Feature 5: Secure File Sharing with WorkDrive */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.4 }}
                whileHover={{ y: -5, scale: 1.02 }}
                className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all border border-gray-100"
              >
                <div className="w-[4.5rem] h-[4.5rem] rounded-xl bg-white border border-gray-200 shadow-md flex items-center justify-center mb-6 p-2">
                  <AppIcon name="sixthFolder" size={44} alt="Secure File Sharing with WorkDrive" />
                </div>
                <h3 className="text-xl text-gray-900 mb-4">Secure File Sharing with WorkDrive</h3>
                <p className="text-gray-600 leading-relaxed">
                  Share sign designs, photos, and marketing files securely with passwords, expiry dates, and permissions.
                </p>
              </motion.div>

              {/* Feature 6: AI-Driven Productivity */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.5 }}
                whileHover={{ y: -5, scale: 1.02 }}
                className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all border border-gray-100"
              >
                <div className="w-[4.5rem] h-[4.5rem] rounded-xl bg-white border border-gray-200 shadow-md flex items-center justify-center mb-6 p-2">
                  <AppIcon name="artificialIntelligence" size={44} alt="AI-Driven Productivity" />
                </div>
                <h3 className="text-xl text-gray-900 mb-4">AI-Driven Productivity</h3>
                <p className="text-gray-600 leading-relaxed">
                  Use Decode AI for instant summaries and document insights, and SmartShow AI for showing intelligence — both designed to make agents and managers more efficient.
                </p>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 relative overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[
            { Icon: CheckCircle, delay: 0, x: '10%', y: '15%' },
            { Icon: Sparkles, delay: 0.5, x: '85%', y: '20%' },
            { Icon: Target, delay: 1, x: '15%', y: '70%' },
            { Icon: TrendingUp, delay: 1.5, x: '80%', y: '75%' },
            { Icon: Zap, delay: 2, x: '50%', y: '10%' },
            { Icon: Shield, delay: 2.5, x: '90%', y: '50%' }
          ].map((item, index) => (
            <motion.div
              key={index}
              className="absolute"
              style={{ left: item.x, top: item.y }}
              initial={{ opacity: 0, scale: 0 }}
              animate={{
                opacity: [0, 0.15, 0],
                scale: [0, 1.2, 0],
                rotate: [0, 180, 360]
              }}
              transition={{
                duration: 8,
                delay: item.delay,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            >
              <item.Icon className="w-20 h-20 text-white" />
            </motion.div>
          ))}
        </div>

        {/* Background gradient shapes */}
        <div className="absolute inset-0">
          <div className="absolute top-1/3 left-1/4 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/3 right-1/4 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl"></div>
        </div>

        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-5xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center space-y-10"
            >
              <div>
                <h2 className="text-4xl md:text-5xl lg:text-6xl text-white mb-6">
                  Start Your Connected Real Estate Journey with RealEsy
                </h2>
                <p className="text-xl md:text-2xl text-blue-100 max-w-3xl mx-auto leading-relaxed">
                  Empower your brokerage or team to manage signage, vendors, and operations — all in one seamless platform.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-6 justify-center">
                <Link to="/contact">
                  <Button
                    size="lg"
                    className="text-lg px-10 py-7 rounded-2xl shadow-2xl hover:shadow-blue-500/50 transition-all"
                    style={{ backgroundColor: '#f58b01' }}
                  >
                    Get Started
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <Button
                  size="lg"
                  variant="outline"
                  className="bg-white text-blue-600 hover:bg-blue-50 border-0 text-lg px-10 py-7 rounded-2xl shadow-2xl hover:shadow-white/20 transition-all"
                >
                  Explore Features
                </Button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
